import {createFeatureSelector, createSelector} from '@ngrx/store';
import {IUserState} from './user.reducer';
import {_DEFAULT_USER_LOGGING, ILoginStepProgress, ILoginSteps, IStateLogger, IUser, IUserLogger} from '../models';

export const selectSharedUserState = createFeatureSelector<IUserState>('user');

//#region GLOBAL
/**
 * Selector to retrieve the current authentication persistence mode from the user state.
 *
 * This selector returns the persistence setting for Firebase Authentication, which can be:
 * - `'local'`: The user's authentication state is stored in the local storage.
 * - `'session'`: The user's authentication state is stored in the session storage (persists only for the current session).
 * - `'none'`: The user's authentication state is not persisted (will be cleared when the page is refreshed).
 *
 * @selector
 * @returns {AuthPersistenceType} The persistence mode for Firebase Authentication.
 */
export const selectAuthPersistence = createSelector(
  selectSharedUserState,
  (state: IUserState) => state.afaPersistence,
);


/**
 * Selector to determine if the page is currently in a loading state.
 *
 * Useful for displaying loaders or disabling UI elements during page-level operations.
 *
 * @returns {boolean} True if the page is loading, false otherwise.
 */
export const selectIsPageLoading = createSelector(
  selectSharedUserState,
  (state: IUserState) => state.isPageLoading,
);

/**
 * Selector to retrieve the complete login progress steps from the user state.
 *
 * This includes each login step indexed by sequence, along with a progress object if available.
 * Defaults to an empty object if no progress data is available.
 *
 * @returns {ILoginSteps} The login steps object including optional progress.
 */
export const selectLoginProgress = createSelector(
  selectSharedUserState,
  (state: IUserState) => state.loginProgress || {} as ILoginSteps,
);

/**
 * Selector to extract only the progress metadata from the login steps.
 *
 * This includes total number of steps, the current step, and the completion percentage.
 * Defaults to an empty object if the progress data is unavailable.
 *
 * @returns {ILoginStepProgress} The progress metadata of the login process.
 */
export const selectLoginProgressProperty = createSelector(
  selectLoginProgress,
  (steps: ILoginSteps) => steps.progress || {} as ILoginStepProgress,
);

//#endregion

//#region USER STATE - USER SELECTORS
/**
 * Selector to determine if the general user loading state is active.
 *
 * This flag is true when user-related data is being loaded (e.g., during login or refresh).
 *
 * @returns {boolean} True if user data is being loaded, false otherwise.
 */
export const selectIsUserLoading = createSelector(
  selectSharedUserState,
  (state: IUserState): boolean => state.isUserLoading,
);

/**
 * Selector to determine if the user details (like extended profile info) are currently being fetched.
 *
 * @returns {boolean} True if user details are loading, false otherwise.
 */
export const selectIsUserDetailsLoading = createSelector(
  selectSharedUserState,
  (state: IUserState): boolean => state.isUserDetailsLoading,
);

export const selectIsUserProfileUpdating = createSelector(
  selectSharedUserState,
  (state: IUserState): boolean => state.isUserProfileUpdating,
);

export const selectLastSuccessfulProfileUpdate = createSelector(
  selectSharedUserState,
  (state: IUserState): Partial<IUser> | null => state.lastSuccessfulProfileUpdate,
);

export const selectUser = createSelector(
  selectSharedUserState,
  (state: IUserState): IUser => state.user || {} as IUser,
);

/**
 * Selector to check whether a user is currently logged in.
 *
 * Useful for guarding routes or showing authenticated user features.
 *
 * @returns {boolean} True if the user is logged in, false otherwise.
 */
export const selectIsUserLoggedIn = createSelector(
  selectSharedUserState,
  (state: IUserState): boolean => state.isUserLoggedIn,
);

/**
 * Selector to check whether the current login was initiated manually by the user.
 *
 * This flag is set when a user actively clicks the login button (vs automatic login).
 *
 * @returns {boolean} True if this is a manual login, false otherwise.
 */
export const selectIsManualLogin = createSelector(
  selectSharedUserState,
  (state: IUserState): boolean => state.isManualLogin,
);

/**
 * Selector that combines both user and user details loading states.
 *
 * Returns true if either general user loading or user details loading is in progress.
 *
 * @returns {boolean} True if any login-related loading is occurring.
 */
export const selectIsLoginLoading = createSelector(
  selectIsUserLoading,
  selectIsUserDetailsLoading,
  selectLoginProgress,
  (isUserLoading: boolean, isUserDetailsLoading: boolean): boolean =>
    isUserLoading || isUserDetailsLoading,
);

/**
 * Selector to determine if a manual login has completed successfully.
 *
 * Returns true when login progress is 100% complete AND the login was initiated manually.
 * This is used to trigger domain change modal display.
 *
 * @returns {boolean} True if manual login is complete, false otherwise.
 */
export const selectIsManualLoginComplete = createSelector(
  selectLoginProgressProperty,
  selectIsManualLogin,
  (loginProgress: ILoginStepProgress, isManualLogin: boolean): boolean => {
    return loginProgress?.percentComplete === 100 && isManualLogin;
  },
);

/**
 * Selector to retrieve the login progress details from the user state.
 *
 * This selector extracts the login steps and their progress, providing
 * information about the current stage of the user's login process.
 *
 * @param {IUserState} state - The current user state from which the login progress is derived.
 * @returns {ILoginSteps} The login steps and progress, including the status of each step.
 */
export const selectUserLoginProgress = createSelector(
  selectSharedUserState,
  (state: IUserState): ILoginSteps => state.loginProgress,
);

//#endregion

//#region USER STATE - PASSWORD SELECTORS
export const selectIsUserResetPasswordLoading = createSelector(
  selectSharedUserState,
  (state: IUserState): boolean => state.isUserResetPasswordLoading,
);

export const selectIsUserResetPasswordSent = createSelector(
  selectSharedUserState,
  (state: IUserState): boolean => state.isUserResetPasswordSent,
);
//#endregion

//#region USER STATE - TECHNICAL SUPPORT - LOGGERS
export const selectUserStateLogger = createSelector(
  selectUser,
  (user: IUser): IStateLogger => user.logState ?? _DEFAULT_USER_LOGGING,
);

export const selectIsLogActionsActive = createSelector(
  selectUserStateLogger,
  (state: IStateLogger): boolean => state.logActions,
);

export const selectIsLogStateActive = createSelector(
  selectUserStateLogger,
  (state: IStateLogger): boolean => state.logState,
);

export const selectUserUserLogger = createSelector(
  selectUser,
  (user: IUser): IUserLogger => user.logUser ?? _DEFAULT_USER_LOGGING,
);

export const selectIsLogInfoActive = createSelector(
  selectUserUserLogger,
  (state: IUserLogger): boolean => state.logInfo,
);

export const selectIsLogErrorsActive = createSelector(
  selectUserUserLogger,
  (state: IUserLogger): boolean => state.logErrors,
);

export const selectIsLogSuccessActive = createSelector(
  selectUserUserLogger,
  (state: IUserLogger): boolean => state.logSuccess,
);
//#endregion

//#region MODALS

/**
 * Selector to determine if the user logout confirmation modal is currently open.
 *
 * This is typically used to show or hide the logout modal in the UI.
 *
 * @returns {boolean} `true` if the logout modal is open, otherwise `false`.
 */
export const selectIsUserLogOutModalOpen = createSelector(
  selectSharedUserState,
  (state: IUserState): boolean => state.isUserLogOutModalOpen,
);

/**
 * Selector to determine if the Global Modal Container is open.
 *
 * This is typically used to show or hide the Global Modal Container in the UI.
 *
 * @returns {boolean} `true` if the logout modal is open, otherwise `false`.
 */
export const selectIsGlobalModalOpen = createSelector(
  selectSharedUserState,
  (state: IUserState): boolean => state.isGlobalModalVisible,
);

/**
 * Selector to determine if the Global Modal Container is open.
 *
 * This is typically used to show or hide the Global Modal Container in the UI.
 *
 * @returns {boolean} `true` if the logout modal is open, otherwise `false`.
 */
export const selectGlobalOpenModals = createSelector(
  selectSharedUserState,
  (state: IUserState): string[] => state.modalType,
);
//#endregion
